import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiRequest, isAuthErrorMessage } from "../../api/client";

export const signup = createAsyncThunk(
  "auth/signup",
  async ({ full_name, email, password, password_confirmation }, thunkApi) => {
    const res = await apiRequest("api/signup", {
      method: "POST",
      body: { full_name, email, password, password_confirmation },
      token: null,
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Signup failed. Please check your details."
      );
    }

    return res.data;
  }
);

export const login = createAsyncThunk(
  "auth/login",
  async ({ email, password }, thunkApi) => {
    const res = await apiRequest("api/login", {
      method: "POST",
      body: { email, password },
      token: null,
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Login failed. Please check your credentials."
      );
    }

    return res.data;
  }
);

export const verifyOtp = createAsyncThunk(
  "auth/verifyOtp",
  async ({ otp }, thunkApi) => {
    const state = thunkApi.getState?.();
    const tokenOverride = state?.auth?.pendingToken || state?.auth?.token;
    const res = await apiRequest("api/verify_otp", {
      method: "POST",
      body: { otp },
      token: tokenOverride,
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "OTP verification failed. Please try again."
      );
    }

    return res.data;
  }
);

export const resendOtp = createAsyncThunk(
  "auth/resendOtp",
  async (_, thunkApi) => {
    const state = thunkApi.getState?.();
    const tokenOverride = state?.auth?.pendingToken || state?.auth?.token;
    const res = await apiRequest("api/resend_otp", {
      method: "POST",
      token: tokenOverride,
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to resend OTP. Please try again."
      );
    }

    return res.data;
  }
);

export const fetchCurrentUser = createAsyncThunk(
  "auth/fetchCurrentUser",
  async (arg, thunkApi) => {
    const res = await apiRequest("api/me", {
      method: "GET",
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to fetch profile."
      );
    }

    return res.data;
  },
  {
    // Prevent duplicate /api/me calls (e.g. React StrictMode remounts).
    condition: (arg, { getState }) => {
      const state = getState();
      const auth = state?.auth;
      if (!auth) return true;
      if (arg?.force) return true;
      if (auth.user) return false; // already have user
      if (auth.status === "loading") return false; // request in flight
      return true;
    },
  }
);

export const updateProfile = createAsyncThunk(
  "auth/updateProfile",
  async (
    {
      full_name,
      address_line1,
    },
    thunkApi
  ) => {
    const res = await apiRequest("api/profile", {
      method: "PUT",
      body: {
        full_name: full_name || undefined,
        address_line1: address_line1 ?? null,
      },
    });

    if (!res.ok) {
      const errors = Array.isArray(res.data?.errors) ? res.data.errors : null;
      return thunkApi.rejectWithValue(
        errors?.join("\n") ||
          res.data?.message ||
          "Failed to update profile."
      );
    }

    return res.data;
  }
);

const initialState = {
  token: (() => {
    try {
      return localStorage.getItem("femtyz_token");
    } catch {
      return null;
    }
  })(),
  pendingToken: (() => {
    try {
      return sessionStorage.getItem("femtyz_pending_token");
    } catch {
      return null;
    }
  })(),
  status: "idle", // idle | loading | succeeded | failed
  error: "",
  user: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.token = null;
      state.pendingToken = null;
      state.user = null;
      state.status = "idle";
      state.error = "";
      try {
        localStorage.removeItem("femtyz_token");
      } catch {
        // ignore
      }
      try {
        sessionStorage.removeItem("femtyz_pending_token");
      } catch {
        // ignore
      }
    },
    clearAuthError(state) {
      state.error = "";
      if (state.status === "failed") {
        state.status = "idle";
      }
    },
    updateBasketComparisonUsage(state, action) {
      if (!state.user) return;

      const patch = action.payload || {};
      ["used", "remaining", "can_compare", "allowed", "unlimited", "monthly_limit"].forEach((key) => {
        if (patch[key] !== undefined) {
          state.user[key] = patch[key];
        }
      });
    },
    setToken(state, action) {
      state.token = action.payload || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signup.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(signup.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = "";
        const token = action.payload?.token;
        if (token) {
          // Signup returns a short-lived token for OTP verification.
          // Do not treat it as an authenticated session until OTP is verified.
          state.pendingToken = token;
          try {
            sessionStorage.setItem("femtyz_pending_token", token);
          } catch {
            // ignore
          }
        }
      })
      .addCase(signup.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Signup failed.";
      })
      .addCase(login.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(login.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = "";
        const token = action.payload?.token;
        if (token) {
          state.token = token;
          try {
            localStorage.setItem("femtyz_token", token);
          } catch {
            // ignore
          }
        }
      })
      .addCase(login.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Login failed.";
      })
      .addCase(verifyOtp.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(verifyOtp.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = "";

        // Promote pending token (or server-provided token) to a real session token.
        const verifiedToken = action.payload?.token || state.pendingToken;
        if (verifiedToken) {
          state.token = verifiedToken;
          try {
            localStorage.setItem("femtyz_token", verifiedToken);
          } catch {
            // ignore
          }
        }

        state.pendingToken = null;
        try {
          sessionStorage.removeItem("femtyz_pending_token");
        } catch {
          // ignore
        }
      })
      .addCase(verifyOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "OTP verification failed.";
      })
      .addCase(resendOtp.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(resendOtp.fulfilled, (state) => {
        state.status = "succeeded";
        state.error = "";
      })
      .addCase(resendOtp.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to resend OTP.";
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.user = action.payload || null;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        const message =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to fetch profile.";
        if (isAuthErrorMessage(message)) {
          state.status = "idle";
          state.error = "";
          state.user = null;
          return;
        }
        state.status = "failed";
        state.error = message;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        const payload = action.payload;
        const candidate =
          payload?.user ||
          payload?.profile ||
          payload?.data ||
          payload;

        // Some API responses return only a message; keep existing user in that case.
        if (candidate && typeof candidate === "object") {
          state.user = {
            ...(state.user || {}),
            ...candidate,
          };
        }
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : "Failed to update profile.";
      });
  },
});

export const { logout, setToken, clearAuthError, updateBasketComparisonUsage } = authSlice.actions;
export default authSlice.reducer;

